/**
 * D255: which sprites the player just watched arrive — the screen's half of
 * a sprite's play-once `transition`.
 *
 * @remarks
 * The pack declares the edge (this sprite, replacing `fromSpriteId`); only the
 * renderer knows whether the player saw it happen, because only the renderer
 * knows what was on screen a moment ago. So the memory is here and nowhere
 * else: the sprites the last scene showed, compared with the ones this scene
 * shows. A sprite arrives by its transition when the last scene showed its
 * `fromSpriteId` and not the sprite, and this one shows the sprite and not
 * its `fromSpriteId`.
 *
 * That one comparison is the whole of "never replayed": a reload or a fresh
 * mount has no last scene, a revisit's last scene was another room's, and
 * the Images toggle's last scene was empty — none of them showed the
 * `fromSpriteId`, so none of them plays. The arrival lasts as long as the
 * scene's sprite set does; the file holds on its last frame, which is the
 * sprite's own, so the next set change swaps in `src` without a seam.
 *
 * @packageDocumentation
 */

import { useState } from 'react';

/** A sprite as far as its arrival cares: who it is, and what it may arrive from. */
export interface ArrivingSprite {
	readonly spriteId: string;
	readonly transition?: { readonly fromSpriteId: string; readonly src: string };
}

/** What the last scene showed, and which of its sprites were arriving. */
interface ShownMemory {
	readonly arrivingIds: ReadonlySet<string>;
	readonly shownIds: readonly string[];
}

const NOTHING_ARRIVING: ReadonlySet<string> = new Set();

/**
 * The sprites of `now` that arrive from a sprite `before` showed.
 *
 * @param shownBefore - The ids the last scene showed
 * @param now - This scene's sprites
 * @returns The ids arriving by their transition
 */
const arrivalsBetween = (shownBefore: ReadonlySet<string>, now: readonly ArrivingSprite[]): ReadonlySet<string> => {
	const shownNow = new Set(now.map((sprite) => sprite.spriteId));

	return new Set(
		now
			.filter(({ spriteId, transition }) =>
				transition !== undefined && !shownBefore.has(spriteId) && shownBefore.has(transition.fromSpriteId) && !shownNow.has(transition.fromSpriteId))
			.map((sprite) => sprite.spriteId),
	);
};

/**
 * Remember the scene's sprites across renders, and say which of them the player just watched arrive.
 *
 * @param sprites - The sprites this scene shows, in any order (`undefined`: none)
 * @returns The ids of the sprites arriving by their transition
 */
export const useArrivingSprites = (sprites: undefined | readonly ArrivingSprite[]): ReadonlySet<string> => {
	const shown = sprites ?? [];
	const shownIds = shown.map((sprite) => sprite.spriteId);
	// React's "information from previous renders" pattern: the memory is state,
	// and it is advanced during render when the scene's set has changed, so the
	// arrival is known in the very render that first shows the sprite.
	const [memory, setMemory] = useState<ShownMemory>({ arrivingIds: NOTHING_ARRIVING, shownIds });

	if (memory.shownIds.join('\n') === shownIds.join('\n')) {
		return memory.arrivingIds;
	}

	const arrivingIds = arrivalsBetween(new Set(memory.shownIds), shown);

	setMemory({ arrivingIds, shownIds });

	return arrivingIds;
};
